import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { trackEvent } from '@services/tracking';
import { ssoConnect, ssoDisconnect } from '@client/new/api/sso.api';
import { useUserStore } from '@client/new/stores/user.store';
import { GetInfluencerQuery } from 'generated/graphql';
import { StepName } from '@client/new/features/update-sign-in-email/useUpdateSignInEmailFlow.hook';
import { getIdToken } from './oauth.utils';
import { SocialProvider } from './account.types';

type UpdateSignInEmailVariant = 'addEmail' | 'disconnectRelay';

const UPDATE_SIGN_IN_EMAIL_VARIANT: Record<
  UpdateSignInEmailVariant,
  UpdateSignInEmailVariant
> = {
  addEmail: 'addEmail',
  disconnectRelay: 'disconnectRelay',
};

type UpdateSignInEmailFlow = { variant: UpdateSignInEmailVariant };

const isApplePrivateRelayEmail = (email: string | null): boolean =>
  email?.endsWith('@privaterelay.appleid.com') ?? false;

const toApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (
      data &&
      typeof data === 'object' &&
      'message' in data &&
      typeof data.message === 'string'
    ) {
      return data.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

type Props = {
  socialAccounts: NonNullable<GetInfluencerQuery['creator']>['socialAccounts'];
  onRefresh: () => Promise<unknown> | void;
};

export const useConnectedAccounts = ({ socialAccounts, onRefresh }: Props) => {
  const { user } = useUserStore();
  const currentEmail: string | null = user?.email ?? null;
  const hasPrivateRelayEmail = isApplePrivateRelayEmail(currentEmail);

  const pendingProviderRef = React.useRef<SocialProvider | null>(null);
  const [pendingProvider, setPendingProvider] =
    React.useState<SocialProvider | null>(null);
  const [confirmDisconnectProvider, setConfirmDisconnectProvider] =
    React.useState<SocialProvider | null>(null);
  const [flow, setFlow] = React.useState<UpdateSignInEmailFlow>();
  const onRefreshRef = React.useRef(onRefresh);

  React.useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const setPending = (provider: SocialProvider | null) => {
    pendingProviderRef.current = provider;
    setPendingProvider(provider);
  };

  const connectedByProvider = React.useMemo(() => {
    const initialState: Record<
      SocialProvider,
      { connected: boolean; email: string | null }
    > = {
      google: { connected: false, email: null },
      apple: { connected: false, email: null },
    };

    for (const account of socialAccounts ?? []) {
      const provider = account.provider?.toLowerCase();

      if (provider === 'google') {
        initialState.google = {
          connected: account.connected,
          email: account.email ?? null,
        };
      }

      if (provider === 'apple') {
        initialState.apple = {
          connected: account.connected,
          email: account.email ?? null,
        };
      }
    }

    return initialState;
  }, [socialAccounts]);

  const handleDisconnect = React.useCallback(
    async (provider: SocialProvider) => {
      if (pendingProviderRef.current) {
        return;
      }

      setPending(provider);

      try {
        await ssoDisconnect({ provider });
        await onRefreshRef.current();
        toast.success('Account disconnected');
      } catch (error) {
        toast.error(toApiErrorMessage(error, 'Unable to disconnect account'));
      } finally {
        setPending(null);
      }
    },
    [],
  );

  const handleConnect = React.useCallback(async (provider: SocialProvider) => {
    if (pendingProviderRef.current) {
      return;
    }

    setPending(provider);

    try {
      const idToken = await getIdToken(provider);
      await ssoConnect({ provider, idToken });
      await onRefreshRef.current();
      toast.success('Account connected');
    } catch (error) {
      toast.error(toApiErrorMessage(error, 'Unable to connect account'));
    } finally {
      setPending(null);
    }
  }, []);

  const openAddEmailFlow = React.useCallback(() => {
    setFlow({ variant: UPDATE_SIGN_IN_EMAIL_VARIANT.addEmail });
    trackEvent({
      type: 'Update Sign-In Email Flow Opened',
      payload: { Entrypoint: 'Apple Relay Banner' },
    });
  }, []);
  const openDisconnectRelayFlow = React.useCallback(() => {
    setFlow({ variant: UPDATE_SIGN_IN_EMAIL_VARIANT.disconnectRelay });
    trackEvent({
      type: 'Update Sign-In Email Flow Opened',
      payload: { Entrypoint: 'Apple Disconnect Guardrail' },
    });
  }, []);
  const closeUpdateSignInEmailFlow = React.useCallback(
    () => setFlow(undefined),
    [],
  );
  const isAddEmailFlowOpen =
    flow?.variant === UPDATE_SIGN_IN_EMAIL_VARIANT.addEmail;
  const isDisconnectRelayFlowOpen =
    flow?.variant === UPDATE_SIGN_IN_EMAIL_VARIANT.disconnectRelay;

  const openConfirmDisconnect = React.useCallback(
    (provider: SocialProvider) => setConfirmDisconnectProvider(provider),
    [],
  );

  const handleConfirmDisconnect = React.useCallback(() => {
    if (!confirmDisconnectProvider) return;
    const provider = confirmDisconnectProvider;
    setConfirmDisconnectProvider(null);
    handleDisconnect(provider);
  }, [confirmDisconnectProvider, handleDisconnect]);

  const cancelConfirmDisconnect = React.useCallback(
    () => setConfirmDisconnectProvider(null),
    [],
  );

  const disconnect = React.useCallback(
    (provider: SocialProvider) => {
      if (provider === 'apple' && hasPrivateRelayEmail) {
        openDisconnectRelayFlow();
        return;
      }
      openConfirmDisconnect(provider);
    },
    [hasPrivateRelayEmail, openDisconnectRelayFlow, openConfirmDisconnect],
  );

  const handleAddEmailFlowCompleted = React.useCallback(() => {
    trackEvent({
      type: 'Update Sign-In Email Flow Completed',
      payload: { Entrypoint: 'Apple Relay Banner' },
    });
    return onRefreshRef.current();
  }, []);

  const handleAddEmailFlowAbandoned = React.useCallback((step: StepName) => {
    trackEvent({
      type: 'Update Sign-In Email Flow Abandoned',
      payload: { Entrypoint: 'Apple Relay Banner', Step: step },
    });
  }, []);

  const handleDisconnectRelayFlowCompleted = React.useCallback(async () => {
    trackEvent({
      type: 'Update Sign-In Email Flow Completed',
      payload: { Entrypoint: 'Apple Disconnect Guardrail' },
    });
    await onRefreshRef.current();
    await handleDisconnect('apple');
  }, [handleDisconnect]);

  const handleDisconnectRelayFlowAbandoned = React.useCallback(
    (step: StepName) => {
      trackEvent({
        type: 'Update Sign-In Email Flow Abandoned',
        payload: { Entrypoint: 'Apple Disconnect Guardrail', Step: step },
      });
    },
    [],
  );

  return {
    connectedByProvider,
    pendingProvider,
    handleConnect,
    disconnect,
    confirmDisconnectProvider,
    handleConfirmDisconnect,
    cancelConfirmDisconnect,
    currentEmail,
    hasPrivateRelayEmail,
    openAddEmailFlow,
    closeUpdateSignInEmailFlow,
    isAddEmailFlowOpen,
    isDisconnectRelayFlowOpen,
    handleAddEmailFlowCompleted,
    handleAddEmailFlowAbandoned,
    handleDisconnectRelayFlowCompleted,
    handleDisconnectRelayFlowAbandoned,
  };
};
